import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import TagItAiDemo from '../components/TagItAiDemo';
import ContactModal from '../components/ContactModal';
import FeaturePreviewLightbox from '../components/FeaturePreviewLightbox';
import heroBanner from '../../assets/knotiv-studio-hero.png';
import positioningBanner from '../../assets/herobanner.svg';
import { featurePillars } from '../content/features';

const pillars = [
  {
    title: 'Governed agents',
    kicker: 'Build with control',
    summary: 'Configure agents, models, tools, policies, and reusable workflows in one governed workspace.',
  },
  {
    title: 'Measured execution',
    kicker: 'See what happens',
    summary: 'Follow runs, token use, estimated cost, latency, tools, and operational outcomes with evidence.',
  },
  {
    title: 'Continuous improvement',
    kicker: 'Make every version better',
    summary: 'Compare agent snapshots, find the cost and performance tradeoffs, and improve deliberately.',
  },
];

const DEFAULT_RAIL_WIDTH = 440;
const MIN_RAIL_WIDTH = 360;
const MAX_RAIL_WIDTH = 440;
const RAIL_WIDTH_STORAGE_KEY = 'knotiv-home-ai-rail-width';

function parseRailWidth(raw: string | null | undefined) {
  if (!raw) return DEFAULT_RAIL_WIDTH;
  const parsed = Number.parseInt(raw, 10);
  if (!Number.isFinite(parsed)) return DEFAULT_RAIL_WIDTH;
  return Math.max(MIN_RAIL_WIDTH, Math.min(MAX_RAIL_WIDTH, parsed));
}

export default function DraftHomePage() {
  const [isContactOpen, setIsContactOpen] = useState(false);
  const [launchPrompt, setLaunchPrompt] = useState<{ id: number; text: string } | null>(null);
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia('(max-width: 960px)').matches;
  });
  const [isClientOpen, setIsClientOpen] = useState(() => {
    if (typeof window === 'undefined') return true;
    return false;
  });
  const [railWidth, setRailWidth] = useState<number>(() => {
    if (typeof window === 'undefined') return DEFAULT_RAIL_WIDTH;
    return parseRailWidth(window.localStorage.getItem(RAIL_WIDTH_STORAGE_KEY));
  });

  useEffect(() => {
    const media = window.matchMedia('(max-width: 960px)');
    const update = () => {
      const nextMobile = media.matches;
      setIsMobile(nextMobile);
      if (nextMobile) {
        setIsClientOpen(false);
      }
    };

    update();
    media.addEventListener('change', update);
    return () => media.removeEventListener('change', update);
  }, []);

  useEffect(() => {
    window.localStorage.setItem(RAIL_WIDTH_STORAGE_KEY, String(railWidth));
  }, [railWidth]);

  const railWidthCss = `${railWidth}px`;

  return (
    <div
      className={`app draft-home-shell ${!isMobile && isClientOpen ? 'is-rail-open' : 'is-rail-collapsed'}`}
      style={{ ['--draft-rail-width' as string]: railWidthCss }}
    >
      <div className="draft-home-content">
        <Header />

        <main className="draft-home-layout">
          <div className="draft-home-main">
            <section id="top" className="section section--surface-light draft-hero">
              <div className="container draft-hero-shell">
                <div className="draft-hero-copy">
                  <h1>
                    Make every <span className="draft-hero-accent">AI agent</span> more effective.
                  </h1>
                  <p className="lead draft-hero-lead">
                    Knotiv gives teams one governed place to build, measure, and improve the AI agents their business
                    depends on.
                  </p>

                  <div className="hero-actions draft-hero-actions">
                    <button
                      type="button"
                      className="btn hero-btn-primary"
                      onClick={() => setIsContactOpen(true)}
                    >
                      Talk to us
                    </button>
                    <button
                      type="button"
                      className="btn hero-btn-secondary"
                      onClick={() => {
                        setIsContactOpen(false);
                        setIsClientOpen(true);
                        setLaunchPrompt({
                          id: Date.now(),
                          text: 'How does Knotiv help teams improve their AI agents?',
                        });
                      }}
                    >
                      Ask Knotiv
                    </button>
                  </div>
                </div>

                <div className="draft-hero-art" aria-label="Knotiv platform preview">
                  <div className="draft-hero-art-screen">
                    <img
                      className="draft-hero-art-image"
                      src={heroBanner}
                      alt="Knotiv workflow configuration preview"
                      loading="eager"
                    />
                    <span className="draft-hero-art-brand-mask" aria-hidden="true" />
                  </div>
                </div>
              </div>
            </section>

            <section id="product" className="section section--surface-dark draft-positioning">
              <div className="container">
                <div className="section-head section-head--wide">
                  <h2>The operating layer for AI agents</h2>
                  <p>Give your teams a shared system for governed creation, trusted measurement, and continuous optimization.</p>
                </div>

                <div className="draft-pillars">
                  {pillars.map((pillar) => (
                    <div key={pillar.title} className="draft-pillar card">
                      <div className="draft-pillar-kicker">{pillar.kicker}</div>
                      <h3 className="draft-pillar-title">{pillar.title}</h3>
                      <p className="draft-pillar-summary">{pillar.summary}</p>
                    </div>
                  ))}
                </div>

                <div className="draft-positioning-visual" aria-label="Knotiv platform journey illustration">
                  <img
                    className="draft-positioning-visual-image"
                    src={positioningBanner}
                    alt="Knotiv agent workflow and measurement illustration"
                    loading="lazy"
                  />
                </div>
              </div>
            </section>

            <section id="proof" className="section section--surface-light draft-proof">
              <div className="draft-proof-shell">
                <div className="section-head section-head--wide">
                  <h2>See the work, not just the answer</h2>
                  <p>Build, measure, and improve every agent with the evidence that matters.</p>
                </div>

                <div className="draft-proof-grid">
                  {featurePillars.map((pillar) => (
                    <article key={pillar.id} className="draft-proof-card">
                      <FeaturePreviewLightbox
                        className="draft-proof-image"
                        image={pillar.image}
                        alt={pillar.imageAlt}
                      />
                      <div className="draft-proof-label">{pillar.eyebrow}</div>
                      <h3 className="draft-proof-title">{pillar.title}</h3>
                      <p className="draft-proof-summary">{pillar.summary}</p>
                      <Link to={`/features#${pillar.id}`} className="draft-proof-link">
                        Explore {pillar.eyebrow}
                      </Link>
                    </article>
                  ))}
                </div>
              </div>
            </section>

            <section id="inquiry" className="section section--surface-dark section-cta">
              <div className="container">
                <div className="mini-cta">
                  <div>
                  <div className="mini-cta-title">Build agents your team can understand and improve.</div>
                  <div className="mini-cta-body">
                      Bring configuration, execution evidence, and optimization into one governed operating layer.
                    </div>
                  </div>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => setIsContactOpen(true)}
                  >
                    Talk to us
                  </button>
                </div>
              </div>
            </section>
          </div>
        </main>

        <Footer />
      </div>

      <aside className="draft-home-rail">
        <TagItAiDemo
          presentation="sidePanel"
          mobilePresentation="fullscreen"
          mobileBreakpointPx={960}
          open={isClientOpen}
          onOpenChange={setIsClientOpen}
          panelWidth={isMobile ? '100vw' : railWidthCss}
          onPanelWidthChange={(nextWidth) => {
            if (!isMobile) {
              setRailWidth(parseRailWidth(nextWidth));
            }
          }}
          resizable={!isMobile}
          tabHeightPercent={isMobile ? 12 : 18}
          minWidthPx={isMobile ? 0 : 360}
          maxWidthPx={MAX_RAIL_WIDTH}
          launchPrompt={launchPrompt}
          onLaunchPromptConsumed={() => setLaunchPrompt(null)}
        />
      </aside>

      <ContactModal open={isContactOpen} onClose={() => setIsContactOpen(false)} />

    </div>
  );
}
